import { cloneElement, isValidElement, useMemo } from 'react';

import { appWithTheme } from '@scorenco/components';
import {
  ApolloService,
  I18nService,
  RouterService,
  RouteLinkProps,
  SentryService,
} from '@scorenco/core';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next/pages';

import { WidgetService } from '../components';

I18nService.init({ useTranslation });

// Inline NextRouterService init (replaces @scorenco/next dependency)
RouterService.init({
  RouteLink: ({
    children,
    passHref = true,
    locale,
    href,
    ...rest
  }: Omit<RouteLinkProps, 'href'> & { href: string }) => {
    const nextLocale = useMemo(() => locale && locale.split('-')[0], [locale]);

    const extendedChildren = useMemo(() => {
      if (!isValidElement(children)) {
        throw new Error('children of a RouteLink must be a ReactElement');
      }

      return cloneElement(children, {
        target: href?.startsWith('http') ? '_blank' : '_self',
      } as Record<string, unknown>);
    }, [children, href]);

    return (
      <NextLink
        legacyBehavior
        passHref={passHref}
        locale={nextLocale}
        prefetch={false}
        href={href}
        {...rest}
      >
        {extendedChildren}
      </NextLink>
    );
  },
  useRouteInfo: () => {
    const router = useRouter();
    return useMemo(
      () => ({
        pathname: router.pathname,
        asPath: router.asPath,
        query: router.query as Record<string, string | undefined>,
      }),
      [router.asPath, router.query]
    );
  },
  useRouteNavigation: () => {
    const router = useRouter();
    return useMemo(
      () => ({
        push: async (options: { pathname: string; locale: string }) =>
          router.push(options.pathname, undefined, {
            locale: options.locale.split('-')[0],
          }),
        replace: async (options: { pathname: string; locale: string }) =>
          router.replace(options.pathname, undefined, {
            locale: options.locale.split('-')[0],
          }),
        back: async () => {
          router.back();
          return true;
        },
      }),
      [router.asPath, router.query]
    );
  },
});

const App = ({ Component, pageProps }: AppProps) => {
  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
      </Head>

      <SentryService>
        <ApolloService initialApolloState={pageProps.initialApolloState}>
          <I18nService.Provider>
            <WidgetService>
              <Component {...pageProps} />
            </WidgetService>
          </I18nService.Provider>
        </ApolloService>
      </SentryService>
    </>
  );
};

export default appWithTheme(appWithTranslation(App));
